import React, {
  ChangeEvent,
  useCallback,
  useMemo,
} from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { useFeature } from 'flagged';

import { OUTFIT_TYPE_EXCEPTIONAL } from 'constants/outfits';
import { NEW_OUTFIT_BEHAVIOUR } from 'feature-flags';
import { fetchAvailable as fetchAvailableStorylets } from 'actions/storylet';
import { fetch as fetchCards } from 'actions/cards';
import { changeOutfit } from 'actions/outfit';
import getOrderedOutfits from 'selectors/outfit/getOrderedOutfits';
import getCanUserChangeOutfit from 'selectors/possessions/getCanUserChangeOutfit';
import { IAppState } from 'types/app';
import { IOutfit } from 'types/outfit';

import SidebarOutfitSelectorDisabled from './SidebarOutfitSelectorDisabled';
import SidebarOutfitSelectorOption from './SidebarOutfitSelectorOption';
import Title from './Title';

function SidebarOutfitSelector({
  canUserChangeOutfit,
  dispatch,
  isChanging,
  isExceptionalFriend,
  outfits,
  responsive,
}: Props) {
  const isDisabledByExceptionalFriendship = useCallback(
    (outfit: IOutfit) => outfit.type === OUTFIT_TYPE_EXCEPTIONAL && !isExceptionalFriend,
    [isExceptionalFriend],
  );

  const hasNewOutfitBehaviour = useFeature(NEW_OUTFIT_BEHAVIOUR);

  const selectedOutfit = useMemo(() => outfits.find(o => o.selected), [outfits]);

  const onChange = useCallback(async (evt: ChangeEvent<HTMLSelectElement>) => {
    if (!canUserChangeOutfit) {
      return;
    }
    await dispatch(changeOutfit(parseInt(evt.target.value, 10), { clearCacheImmediately: false }));
    dispatch(fetchAvailableStorylets({ setIsFetching: true }));
    dispatch(fetchCards());
  }, [
    canUserChangeOutfit,
    dispatch,
  ]);

  const sortedOutfits: (IOutfit & { disabled: boolean })[] = useMemo(
    () => outfits
      .map(o => ({ ...o, disabled: isDisabledByExceptionalFriendship(o) }))
      .sort((a, b) => {
        if (a.disabled === b.disabled) {
          return 0;
        }
        if (a.disabled) {
          return 1;
        }
        return -1;
      }),
    [
      isDisabledByExceptionalFriendship,
      outfits,
    ],
  );

  if (!hasNewOutfitBehaviour) {
    return null;
  }

  if (!selectedOutfit) {
    return null;
  }

  if (!canUserChangeOutfit) {
    return <SidebarOutfitSelectorDisabled />;
  }

  return (
    <div>
      <Title />
      <select
        className={classnames(
          'form__control js-selector outfit-selector',
          responsive && 'outfit-selector--responsive',
          isChanging && 'outfit-selector--is-changing',
        )}
        name="outfit"
        value={selectedOutfit?.id ?? ''}
        onChange={onChange}
      >
        {sortedOutfits.map(outfit => (
          <SidebarOutfitSelectorOption
            key={outfit.id}
            outfit={outfit}
            disabled={outfit.disabled}
          />
        ))}
      </select>
    </div>
  );
}

interface OwnProps {
  /// These props are sent to getCanUserChangeOutfit(state, props) but are linted as unused here
  /* eslint-disable react/no-unused-prop-types */
  areOutfitsLockable: boolean,
  doesStoryletStateLockOutfits: boolean,
  /* eslint-enable react/no-unused-prop-types */
  responsive?: boolean,
}

const mapStateToProps = (state: IAppState, props: OwnProps) => ({
  canUserChangeOutfit: getCanUserChangeOutfit(state, props),
  isChanging: state.outfit.isChanging,
  isExceptionalFriend: state.fate.isExceptionalFriend,
  outfits: getOrderedOutfits(state),
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & {
  dispatch: Function, // eslint-disable-line
};

export default connect(mapStateToProps)(SidebarOutfitSelector);
